import { useState, useEffect, useCallback } from 'react';
import { gradeService } from '../services/gradeService';

export const useGrades = (params = {}) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchGrades = useCallback(async (fetchParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await gradeService.getMyGrades({
        ...params,
        ...fetchParams
      });
      
      setGrades(response.data.grades || []);
      
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (err) {
      // Handle rate limiting specifically
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and refresh the page.');
        console.warn('Rate limited, stopping further requests');
        return; // Don't retry on rate limit
      }
      setError(err.response?.data?.message || 'Failed to fetch grades');
      console.error('Error fetching grades:', err);
    } finally {
      setLoading(false);
    }
  }, [params.limit, params.page, params.subjectId, params.academicYear, params.term, params.type]);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  const createGrade = async (data) => {
    try {
      const response = await gradeService.createGrade(data);
      await fetchGrades(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create grade';
    }
  };

  const updateGrade = async (gradeId, data) => {
    try {
      const response = await gradeService.updateGrade(gradeId, data);
      await fetchGrades(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to update grade';
    }
  };

  const deleteGrade = async (gradeId) => {
    try {
      await gradeService.deleteGrade(gradeId);
      await fetchGrades(); // Refresh the list
    } catch (err) {
      throw err.response?.data?.message || 'Failed to delete grade';
    }
  };

  const bulkCreateGrades = async (data) => {
    try {
      const response = await gradeService.bulkCreateGrades(data);
      await fetchGrades(); // Refresh the list
      return response.data;
    } catch (err) {
      throw err.response?.data?.message || 'Failed to create grades';
    }
  };

  const refreshGrades = () => {
    fetchGrades();
  };

  const loadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchGrades({ page: pagination.page + 1 });
    }
  };

  return {
    grades,
    loading,
    error,
    pagination,
    createGrade,
    updateGrade,
    deleteGrade,
    bulkCreateGrades,
    refreshGrades,
    loadMore,
    fetchGrades
  };
};

export const useStudentGrades = (studentId, params = {}) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStudentGrades = useCallback(async () => {
    if (!studentId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await gradeService.getStudentGrades(studentId, params);
      setGrades(response.data.grades || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch student grades');
      console.error('Error fetching student grades:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, params]);

  useEffect(() => {
    fetchStudentGrades();
  }, [fetchStudentGrades]);

  return {
    grades,
    loading,
    error,
    refreshStudentGrades: fetchStudentGrades
  };
};

export const useClassGrades = (classId, params = {}) => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchClassGrades = useCallback(async () => {
    if (!classId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await gradeService.getClassGrades(classId, params);
      setGrades(response.data.grades || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch class grades');
      console.error('Error fetching class grades:', err);
    } finally {
      setLoading(false);
    }
  }, [classId, params]);

  useEffect(() => {
    fetchClassGrades();
  }, [fetchClassGrades]);

  return {
    grades,
    loading,
    error,
    refreshClassGrades: fetchClassGrades
  };
};

export const useReportCard = (studentId, termId) => {
  const [reportCard, setReportCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReportCard = useCallback(async () => {
    if (!studentId || !termId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await gradeService.getReportCard(studentId, termId);
      setReportCard(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch report card');
      console.error('Error fetching report card:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId, termId]);

  useEffect(() => {
    fetchReportCard();
  }, [fetchReportCard]);

  return {
    reportCard,
    loading,
    error,
    refreshReportCard: fetchReportCard
  };
};

export const useGradeStats = (params = {}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGradeStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await gradeService.getGradeStats(params);
      setStats(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch grade statistics');
      console.error('Error fetching grade statistics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGradeStats(params);
  }, [fetchGradeStats]);

  return {
    stats,
    loading,
    error,
    refreshGradeStats: fetchGradeStats
  };
};
